import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@/shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";

interface ColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
  presets?: string[];
  className?: string;
  swatchClassName?: string;
  ariaLabel?: string;
}

const DEFAULT_PRESETS = [
  "#22d3ee",
  "#5b21b6",
  "#fbbf24",
  "#ef4444",
  "#10b981",
  "#f97316",
  "#8b5cf6",
  "#ec4899",
];

function hexToHsv(hex: string): { h: number; s: number; v: number } {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return { h: 0, s: 0, v: 0 };
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  return { h, s, v: max };
}

function hsvToHex(h: number, s: number, v: number): string {
  const c = v * s;
  const hh = h / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hh >= 0 && hh < 1) {
    r = c;
    g = x;
  } else if (hh < 2) {
    r = x;
    g = c;
  } else if (hh < 3) {
    g = c;
    b = x;
  } else if (hh < 4) {
    g = x;
    b = c;
  } else if (hh < 5) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const m = v - c;
  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

const HEX_RE = /^#?[a-fA-F0-9]{6}$/;

export function ColorPicker({
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  className,
  swatchClassName,
  ariaLabel,
}: ColorPickerProps) {
  const { t } = useTranslation();
  const [hsv, setHsv] = useState(() => hexToHsv(value));
  const [hexInput, setHexInput] = useState(value);
  const svRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);

  // 外部 value 变了(比如点了"恢复默认"),把 hsv 同步过来
  useEffect(() => {
    if (!HEX_RE.test(value)) return;
    const current = hsvToHex(hsv.h, hsv.s, hsv.v).toLowerCase();
    if (value.toLowerCase() !== current) {
      setHsv(hexToHsv(value));
      setHexInput(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function commit(h: number, s: number, v: number) {
    const next = { h, s, v };
    const hex = hsvToHex(h, s, v);
    setHsv(next);
    setHexInput(hex);
    onChange(hex);
  }

  function svDrag(e: React.PointerEvent) {
    if (!svRef.current) return;
    const rect = svRef.current.getBoundingClientRect();
    const move = (clientX: number, clientY: number) => {
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
      const y = Math.max(0, Math.min(rect.height, clientY - rect.top));
      commit(hsv.h, x / rect.width, 1 - y / rect.height);
    };
    move(e.clientX, e.clientY);
    const onMove = (ev: PointerEvent) => move(ev.clientX, ev.clientY);
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  function hueDrag(e: React.PointerEvent) {
    if (!hueRef.current) return;
    const rect = hueRef.current.getBoundingClientRect();
    const move = (clientX: number) => {
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
      commit((x / rect.width) * 360, hsv.s, hsv.v);
    };
    move(e.clientX);
    const onMove = (ev: PointerEvent) => move(ev.clientX);
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  function commitHexInput() {
    if (HEX_RE.test(hexInput)) {
      const next = (hexInput.startsWith("#") ? hexInput : `#${hexInput}`).toLowerCase();
      setHsv(hexToHsv(next));
      setHexInput(next);
      onChange(next);
    } else {
      setHexInput(value);
    }
  }

  const hueColor = hsvToHex(hsv.h, 1, 1);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel ?? t("colorPicker.ariaLabel", { value })}
          className={cn(
            "h-9 w-9 cursor-pointer rounded border border-border/60 bg-transparent transition-colors hover:border-primary/50",
            swatchClassName
          )}
          style={{ backgroundColor: HEX_RE.test(value) ? value : undefined }}
        />
      </PopoverTrigger>
      <PopoverContent className={cn("w-60 space-y-3 p-3", className)} sideOffset={6}>
        <div
          ref={svRef}
          onPointerDown={svDrag}
          className="relative h-32 w-full cursor-crosshair touch-none rounded-md"
          style={{
            backgroundColor: hueColor,
            backgroundImage:
              "linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent)",
          }}
        >
          <div
            className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)]"
            style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
          />
        </div>

        <div
          ref={hueRef}
          onPointerDown={hueDrag}
          className="relative h-3 w-full cursor-pointer touch-none rounded-full"
          style={{
            backgroundImage:
              "linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)",
          }}
        >
          <div
            className="pointer-events-none absolute top-1/2 h-4 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.4)]"
            style={{ left: `${(hsv.h / 360) * 100}%` }}
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-2xs text-muted-foreground">HEX</span>
          <Input
            value={hexInput}
            onChange={(e) => setHexInput(e.target.value)}
            onBlur={commitHexInput}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitHexInput();
              }
            }}
            maxLength={7}
            className="h-8 flex-1 font-mono text-xs"
            spellCheck={false}
          />
        </div>

        {presets.length > 0 && (
          <div className="grid grid-cols-8 gap-1.5">
            {presets.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={t("colorPicker.presetAria", { color: c })}
                title={c}
                className={cn(
                  "h-5 w-5 cursor-pointer rounded border transition-transform hover:scale-110",
                  c.toLowerCase() === value.toLowerCase() ? "border-primary" : "border-border/60"
                )}
                style={{ backgroundColor: c }}
                onClick={() => {
                  setHsv(hexToHsv(c));
                  setHexInput(c);
                  onChange(c);
                }}
              />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
